import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { useUppy } from '@lyearn/shared/hooks/useUppy';
import { Stack } from '@lyearn/ui';
import * as ContentListModal from '@/components/ContentListModal';
import { useAppConfig } from '@/hooks/useAppConfig';
import useDebounceState from '@/hooks/useDebounceState';
import ImageList from '@/modules/corporate/components/ImageList';
import { CoverType } from '@/modules/corporate/components/ImageList/types';
import { getUploadedCoverItems } from '@/modules/corporate/components/ImageList/utils';

import ListLoader from './ListLoader';
import { AddCoverDialogProps } from './types';
import { getI18nLabels, IMAGE_LIST_OVERRIDES, modalClasses } from './utils';

const AddCoverDialog: React.FC<AddCoverDialogProps> = (props) => {
  const { t } = useTranslation();
  const { cover, onClose, onSubmit, i18n, disableRestoreFocus, staticImageType } = props;

  const [selectedCard, setSelectedCard] = useState<CoverType>({
    url: cover?.url ?? '',
    name: cover?.name ?? '',
  });
  const [searchInput, setSearchInput, searchString] = useDebounceState('');
  const { files, uppy, clearAll } = useUppy({ referenceId: 'add-cover-dialog' });
  const {
    site: { disabledUnsplash },
  } = useAppConfig();

  const uploadedItems: CoverType[] = useMemo(() => getUploadedCoverItems(files), [files]);

  const _onSubmit = useCallback(() => {
    onSubmit(selectedCard);
    onClose();
    clearAll();
  }, [clearAll, onClose, onSubmit, selectedCard]);

  const onCloseModal = useCallback(() => {
    onClose();
    clearAll();
  }, [clearAll, onClose]);

  const { actionText, headerTitle, searchPlaceholder } = getI18nLabels(t, i18n);

  return (
    <ContentListModal.Main
      action={{
        text: actionText,
        onSubmit: _onSubmit,
      }}
      className="h-full w-full"
      classes={modalClasses}
      dialogProps={{ disableRestoreFocus }}
      header={{
        title: headerTitle,
      }}
      searchBar={
        disabledUnsplash || staticImageType
          ? undefined
          : {
              placeholder: searchPlaceholder,
              value: searchInput,
              onChange: setSearchInput,
            }
      }
      onClose={onCloseModal}>
      <Stack className="flex-1" justifyContent="center">
        <ImageList
          ListLoader={ListLoader}
          coverUrl={cover?.url}
          items={uploadedItems}
          overrides={IMAGE_LIST_OVERRIDES}
          searchString={searchString}
          selectedCard={selectedCard}
          setCard={setSelectedCard}
          showRandom={false}
          staticImageType={staticImageType}
          uppy={uppy}
        />
      </Stack>
    </ContentListModal.Main>
  );
};

export default AddCoverDialog;
